import { Controller } from "@hotwired/stimulus"

// Lists the header names of a just-selected claim file so you can pick the
// right headers line number without opening the file.
const CSV_PREVIEW_BYTES = 1024 * 1024
const MAX_PREVIEW_BYTES = 25 * 1024 * 1024

export default class extends Controller {
  static targets = ["file", "lineNumber", "preview", "list", "message"]
  static values = { previewUrl: String }

  connect() {
    this.rows = null
    this.requestId = 0
  }

  async fileChanged() {
    const file = this.fileTarget.files[0]
    this.rows = null
    // Selecting another file while a preview is in flight must not let the
    // older response render headers for a file that is no longer selected.
    const requestId = ++this.requestId

    if (!file) {
      this.previewTarget.classList.add("hidden")
      return
    }

    const isCsv = file.name.toLowerCase().endsWith(".csv")
    if (!isCsv && file.size > MAX_PREVIEW_BYTES) {
      this.showMessage("That file is too big to preview headers for.")
      return
    }

    this.showMessage("Reading headers...")

    const body = new FormData()
    // A CSV only needs its first lines, so avoid uploading a huge file twice.
    body.append("file", isCsv ? file.slice(0, CSV_PREVIEW_BYTES) : file, file.name)

    try {
      const response = await fetch(this.previewUrlValue, {
        method: "POST",
        headers: {
          "Accept": "application/json",
          "X-CSRF-Token": globalThis.document?.querySelector("meta[name='csrf-token']")?.content
        },
        body: body
      })
      const data = await response.json()
      if (requestId !== this.requestId) return

      if (!response.ok) {
        this.showMessage(data.error || "Sorry, we couldn't read the headers from that file.")
        return
      }

      this.rows = data.rows || []
      this.render()
    } catch (e) {
      if (requestId === this.requestId) {
        this.showMessage("Sorry, we couldn't read the headers from that file.")
      }
    }
  }

  render() {
    if (!this.rows) return

    const lineNumber = parseInt(this.lineNumberTarget.value, 10) || 1
    const headers = (this.rows[lineNumber - 1] || []).filter((header) => header !== "")

    if (headers.length === 0) {
      this.showMessage(`No headers found on line ${lineNumber}.`)
      return
    }

    this.messageTarget.textContent = `${headers.length} headers on line ${lineNumber}:`
    this.listTarget.replaceChildren(
      ...headers.map((header) => {
        const item = globalThis.document.createElement("li")
        item.textContent = header
        return item
      })
    )
    this.previewTarget.classList.remove("hidden")
  }

  showMessage(message) {
    this.messageTarget.textContent = message
    this.listTarget.replaceChildren()
    this.previewTarget.classList.remove("hidden")
  }
}
